import React, { ReactNode } from 'react';
import { Analytics, SegmentEvent } from 'global';
import storefrontAnalytics from 'storefront-analytics/dist/analytics';
import log from '../../src/shared/util/log';
import AnalyticsContext from './context/AnalyticsContext';

export const ANALYTICS_ACTION_IMPRESSION = 'impression';
export const ANALYTICS_ACTION_CLICK = 'click';
export const ANALYTICS_ACTION_EXPAND = 'expand';
export const ANALYTICS_LABEL_SUFFIX_LEARN_MORE = '_learn_more';
export const ANALYTICS_LABEL_SUFFIX_HEADER = '_header';

const defaultAnalytics: Analytics = {
  trackEvent: () => {},
  trackOnboarding: () => {},
  trackStarterGuide: () => {},
  trackRainbow: () => {},
  trackConfiguredForm: () => {},
  trackError: () => {},
};

export const AnalyticsProvider = ({
  analytics = defaultAnalytics,
  children,
}: {
  analytics?: Analytics;
  children: ReactNode;
}) => {
  const isBrowser = typeof window !== 'undefined';

  const isWebView =
    isBrowser &&
    (/(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(navigator.userAgent) || // iOS WebView
      /(wv)|(WebView)/.test(navigator.userAgent)); // Other WebView implementations

  const getPlatform = () => {
    if (!isBrowser) return 'Unknown';
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      return 'Windows';
    } else if (userAgent.includes('mac')) {
      return 'Mac';
    } else if (userAgent.includes('linux')) {
      return 'Linux';
    } else if (
      userAgent.includes('iphone') ||
      userAgent.includes('ipad') ||
      userAgent.includes('ipod')
    ) {
      return 'iOS';
    } else if (userAgent.includes('android')) {
      return 'Android';
    } else {
      return 'Unknown';
    }
  };
  const platform = getPlatform();

  return (
    <AnalyticsContext.Provider
      value={{
        trackEvent: (...args: any[]) => {
          analytics.trackEvent(...args);
        },
        trackOnboarding: (eventAction: string, eventLabel: string, petId?: number) => {
          analytics.trackEvent(
            'new-pet-profile',
            eventAction,
            eventLabel,
            null,
            petId ? { sourceView: 'pet-profile-v2', petId } : { sourceView: 'pet-profile-v2' },
          );
        },
        trackStarterGuide: (eventAction: string, eventLabel: string) => {
          analytics.trackEvent('puppyGuide', eventAction, eventLabel);
        },
        trackError: (eventAction: string, eventLabel: string, petId?: number) => {
          analytics.trackEvent(
            'error',
            eventAction,
            eventLabel,
            null,
            petId ? { sourceView: 'pet-profile-v2', petId } : { sourceView: 'pet-profile-v2' },
          );
        },
        trackRainbow: (event: SegmentEvent) => {
          if (storefrontAnalytics) {
            storefrontAnalytics.segmentTrack(event.name, {
              eventCategory: event.category,
              eventLabel: event.label,
              eventAction: event.action,
            });
          }
          try {
            dynatrace.sendBizEvent(event.name, {
              event_category: event.category,
              event_label: event.label,
              event_action: event.action,
              event_name: event.name,
              is_webview: isWebView,
              platform: platform,
            });
          } catch {
            log.error(new Error(`fetchError: Error sending dynatrace business event`));
          }
        },
        trackConfiguredForm: (event: SegmentEvent) => {
          if (storefrontAnalytics) {
            storefrontAnalytics.segmentTrack(event.name, {
              eventCategory: event.category,
              eventLabel: event.label,
              eventAction: event.action,
            });
          }
          try {
            dynatrace.sendBizEvent(event.name, {
              event_category: event.category,
              event_label: event.label,
              event_action: event.action,
              event_name: event.name,
              is_webview: isWebView,
              platform: platform,
            });
          } catch {
            log.error(new Error(`fetchError: Error sending dynatrace business event`));
          }
        },
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
